import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Plug,
  TestTube,
  Cpu,
  Sparkles,
  Hash,
  MessageSquare,
} from "lucide-react";
import { useUIPrefsStore } from "@/store/uiPrefsSlice";
import { api } from "@/api/client";
import ConnectionModelsDrawer from "@/components/settings/ConnectionModelsDrawer";
import { clsx } from "clsx";
import {
  listAIConnections,
  createAIConnection,
  updateAIConnection,
  deleteAIConnection,
  testAIConnection,
  getAIDefaults,
  type AIConnection,
  type AIConnectionInput,
  type AIDefaultsResponse,
  type AIProvider,
  type AuthType,
  type AITestResponse,
} from "@/api/aiConnections";

const PROVIDERS: { value: AIProvider; label: string; defaultUrl: string }[] = [
  { value: "OLLAMA", label: "Ollama", defaultUrl: "http://localhost:11434" },
  { value: "OPENAI", label: "OpenAI", defaultUrl: "https://api.openai.com" },
];

const AUTH_TYPES: { value: AuthType; label: string }[] = [
  { value: "NONE", label: "None" },
  { value: "BEARER", label: "Bearer Token" },
  { value: "API_KEY", label: "API Key" },
  { value: "BASIC", label: "Basic Auth" },
];

interface HeaderEntry {
  key: string;
  value: string;
}

function emptyForm(provider: AIProvider = "OLLAMA"): AIConnectionInput {
  return {
    name: "",
    provider,
    base_url: PROVIDERS.find((p) => p.value === provider)!.defaultUrl,
    auth_type: "NONE",
    api_key: "",
    username: "",
    password: "",
    headers: null,
    is_active: true,
  };
}

export default function ConnectionsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AIConnection | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [modelsFor, setModelsFor] = useState<AIConnection | null>(null);
  const [form, setForm] = useState<AIConnectionInput>(emptyForm());
  const [headerEntries, setHeaderEntries] = useState<HeaderEntry[]>([]);
  const [testResult, setTestResult] = useState<AITestResponse | null>(null);
  const qnaMenuEnabled = useUIPrefsStore((s) => s.qnaMenuEnabled);
  const setQnaMenuEnabled = useUIPrefsStore((s) => s.setQnaMenuEnabled);

  const { data: connections = [], isLoading } = useQuery<AIConnection[]>({
    queryKey: ["ai-connections"],
    queryFn: listAIConnections,
  });

  const { data: defaults } = useQuery<AIDefaultsResponse>({
    queryKey: ["ai-defaults"],
    queryFn: getAIDefaults,
  });

  // Server-side app settings (persisted in DB, read by the Celery pipeline).
  const { data: appSettings } = useQuery<{
    auto_aspect_extraction_enabled: boolean;
  }>({
    queryKey: ["app-settings"],
    queryFn: () => api.get("/settings/app"),
  });
  const autoAspectMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put("/settings/app", { auto_aspect_extraction_enabled: enabled }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["app-settings"] }),
  });

  const createMut = useMutation({
    mutationFn: createAIConnection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] });
      queryClient.invalidateQueries({ queryKey: ["ai-defaults"] });
      closeForm();
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AIConnectionInput }) =>
      updateAIConnection(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] });
      queryClient.invalidateQueries({ queryKey: ["ai-defaults"] });
      closeForm();
    },
  });
  const deleteMut = useMutation({
    mutationFn: deleteAIConnection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] });
      queryClient.invalidateQueries({ queryKey: ["ai-defaults"] });
    },
  });
  const testMut = useMutation({
    mutationFn: testAIConnection,
    onSuccess: (data) => setTestResult(data),
    onError: (err: any) =>
      setTestResult({
        ok: false,
        message: "Request gagal",
        detail: err?.message ?? String(err),
      }),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setHeaderEntries([]);
    setTestResult(null);
    setShowForm(true);
  };

  const openEdit = (conn: AIConnection) => {
    setEditing(conn);
    setForm({
      name: conn.name,
      provider: conn.provider,
      base_url: conn.base_url,
      auth_type: conn.auth_type,
      api_key: conn.api_key ?? "",
      username: conn.username ?? "",
      password: conn.password ?? "",
      headers: conn.headers ?? null,
      is_active: conn.is_active,
    });
    setHeaderEntries(
      conn.headers
        ? Object.entries(conn.headers).map(([key, value]) => ({ key, value }))
        : [],
    );
    setTestResult(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setTestResult(null);
  };

  const headersObject = useMemo(() => {
    const obj: Record<string, string> = {};
    headerEntries.forEach((h) => {
      if (h.key.trim()) obj[h.key.trim()] = h.value;
    });
    return Object.keys(obj).length > 0 ? obj : null;
  }, [headerEntries]);

  const payload = (): AIConnectionInput => ({
    ...form,
    headers: headersObject,
    // Clean out irrelevant auth fields
    api_key:
      form.auth_type === "BEARER" || form.auth_type === "API_KEY"
        ? form.api_key || null
        : null,
    username: form.auth_type === "BASIC" ? form.username || null : null,
    password: form.auth_type === "BASIC" ? form.password || null : null,
  });

  const handleSave = () => {
    const data = payload();
    if (editing) {
      updateMut.mutate({ id: editing.id, data });
    } else {
      createMut.mutate(data);
    }
  };

  const handleTest = () => {
    const { name, is_active, ...rest } = payload();
    testMut.mutate(rest);
  };

  return (
    <div className="space-y-5">
      {/* ── Pengaturan Menu (visibilitas menu) ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">
          Pengaturan Menu
        </h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Aktif/nonaktifkan menu tertentu di navigasi.
        </p>
        <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-4 py-3">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-4 w-4 text-violet-600" />
            <div>
              <p className="text-sm font-medium text-gray-800">
                Menu Tanya AI (QNA)
              </p>
              <p className="text-xs text-gray-500">
                Menampilkan menu Tanya AI berbasis dokumen di sidebar.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={qnaMenuEnabled}
            onClick={() => setQnaMenuEnabled(!qnaMenuEnabled)}
            className={clsx(
              "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
              qnaMenuEnabled ? "bg-violet-600" : "bg-gray-300",
            )}
          >
            <span
              className={clsx(
                "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                qnaMenuEnabled ? "translate-x-5" : "translate-x-0.5",
              )}
            />
          </button>
        </div>
      </div>

      {/* ── Pengaturan Pemrosesan AI ── */}
      {/* <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">Pengaturan Pemrosesan AI</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Atur perilaku pipeline AI di latar belakang.
        </p>
        <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-4 py-3">
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-violet-600" />
            <div>
              <p className="text-sm font-medium text-gray-800">
                Auto-ekstraksi aspek saat upload
              </p>
              <p className="text-xs text-gray-500">
                Saat dokumen diupload, isi aspek node taksonomi otomatis dibuat (DRAFT) dari
                hasil ekstraksi. Nonaktifkan untuk mempercepat antrian; aspek dapat dibuat
                manual lewat tombol "Generate dengan AI".
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!appSettings?.auto_aspect_extraction_enabled}
            disabled={autoAspectMutation.isPending || !appSettings}
            onClick={() =>
              autoAspectMutation.mutate(!appSettings?.auto_aspect_extraction_enabled)
            }
            className={clsx(
              "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50",
              appSettings?.auto_aspect_extraction_enabled ? "bg-violet-600" : "bg-gray-300",
            )}
          >
            <span
              className={clsx(
                "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                appSettings?.auto_aspect_extraction_enabled ? "translate-x-5" : "translate-x-0.5",
              )}
            />
          </button>
        </div>
      </div> */}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Connections</h1>
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            Kelola koneksi ke penyedia AI (Ollama, OpenAI)
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Tambah Koneksi
        </button>
      </div>

      {/* Active defaults banner */}
      {defaults && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(["generate", "embed"] as const).map((role) => {
            const d = defaults[role];
            const isSet = d.source === "db";
            const Icon = role === "generate" ? Sparkles : Hash;
            return (
              <div
                key={role}
                className={clsx(
                  "rounded-lg border p-3",
                  isSet
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-gray-200 bg-gray-50",
                )}
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                  <Icon className="h-4 w-4" />
                  {role === "generate"
                    ? "Default Generate (LLM)"
                    : "Default Embedding"}
                </div>
                {isSet ? (
                  <>
                    <p className="mt-1 truncate text-sm font-medium text-gray-900">
                      {d.connection_name ? `${d.connection_name} · ` : ""}
                      <span className="font-mono">{d.model}</span>
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {d.base_url}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    Belum diset. Pilih model default dari salah satu koneksi di
                    bawah.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <p className="text-sm text-gray-500">Memuat…</p>
      ) : connections.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-400">
          Belum ada koneksi. Klik "Tambah Koneksi" untuk membuat.
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {connections.map((conn) => (
            <li
              key={conn.id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm"
              onClick={() => setModelsFor(conn)}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                      {conn.provider}
                    </span>
                    <h3 className="truncate font-semibold text-gray-900">
                      {conn.name}
                    </h3>
                  </div>
                  <p className="mt-1 truncate text-xs text-gray-500">
                    {conn.base_url}
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                    <span>Auth: {conn.auth_type}</span>
                    <span className="flex items-center gap-1">
                      <Cpu className="h-3 w-3" />
                      {conn.enabled_models?.length ?? 0} model aktif
                    </span>
                    {!conn.is_active && (
                      <span className="rounded bg-gray-100 px-1.5 text-gray-500">
                        nonaktif
                      </span>
                    )}
                    {conn.is_default_generate && (
                      <span className="flex items-center gap-1 rounded bg-emerald-100 px-1.5 font-semibold text-emerald-700">
                        <Sparkles className="h-3 w-3" />
                        GEN: {conn.default_gen_model}
                      </span>
                    )}
                    {conn.is_default_embed && (
                      <span className="flex items-center gap-1 rounded bg-emerald-100 px-1.5 font-semibold text-emerald-700">
                        <Hash className="h-3 w-3" />
                        EMB: {conn.default_embed_model}
                      </span>
                    )}
                  </p>
                </div>
                <div
                  className="flex gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => openEdit(conn)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Hapus koneksi "${conn.name}"?`))
                        deleteMut.mutate(conn.id);
                    }}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Drawer / Modal form */}
      {modelsFor && (
        <ConnectionModelsDrawer
          connection={modelsFor}
          onClose={() => setModelsFor(null)}
        />
      )}

      {showForm && createPortal(
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={closeForm} />
          <div className="flex w-full max-w-lg flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-bold text-gray-900">
                {editing ? "Edit Koneksi" : "Tambah Koneksi"}
              </h2>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {/* Provider */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Provider <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {PROVIDERS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          provider: p.value,
                          base_url: p.defaultUrl,
                        }))
                      }
                      className={clsx(
                        "flex-1 rounded-lg border px-3 py-2 text-sm font-medium",
                        form.provider === p.value
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Nama Koneksi <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Contoh: Ollama Local"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {/* Base URL */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.base_url}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, base_url: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                />
              </div>

              {/* Auth Type */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Auth
                </label>
                <select
                  value={form.auth_type}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      auth_type: e.target.value as AuthType,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {AUTH_TYPES.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Auth fields (conditional) */}
              {(form.auth_type === "BEARER" ||
                form.auth_type === "API_KEY") && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {form.auth_type === "BEARER" ? "Token" : "API Key"}
                  </label>
                  <input
                    type="password"
                    value={form.api_key ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, api_key: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                  />
                </div>
              )}

              {form.auth_type === "BASIC" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Username
                    </label>
                    <input
                      type="text"
                      value={form.username ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, username: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Password
                    </label>
                    <input
                      type="password"
                      value={form.password ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, password: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Headers (optional) */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">
                    Headers (opsional)
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setHeaderEntries((h) => [...h, { key: "", value: "" }])
                    }
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    + Tambah
                  </button>
                </div>
                {headerEntries.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    Tidak ada header tambahan.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {headerEntries.map((h, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={h.key}
                          placeholder="Header name"
                          onChange={(e) =>
                            setHeaderEntries((prev) =>
                              prev.map((x, idx) =>
                                idx === i ? { ...x, key: e.target.value } : x,
                              ),
                            )
                          }
                          className="flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-mono"
                        />
                        <input
                          type="text"
                          value={h.value}
                          placeholder="Value"
                          onChange={(e) =>
                            setHeaderEntries((prev) =>
                              prev.map((x, idx) =>
                                idx === i ? { ...x, value: e.target.value } : x,
                              ),
                            )
                          }
                          className="flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-mono"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setHeaderEntries((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            )
                          }
                          className="rounded p-1.5 text-gray-400 hover:bg-gray-100"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Active */}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, is_active: e.target.checked }))
                  }
                />
                Aktif
              </label>

              {/* Test result */}
              {testResult && (
                <div
                  className={clsx(
                    "rounded-lg border p-3 text-sm",
                    testResult.ok
                      ? "border-green-200 bg-green-50 text-green-800"
                      : "border-red-200 bg-red-50 text-red-800",
                  )}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    {testResult.ok ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                    {testResult.message}
                    {testResult.status_code && (
                      <span className="font-normal">
                        (HTTP {testResult.status_code})
                      </span>
                    )}
                  </div>
                  {testResult.detail && (
                    <p className="mt-1 break-all text-xs opacity-80">
                      {testResult.detail}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={handleTest}
                disabled={testMut.isPending || !form.base_url}
                className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <TestTube className="h-4 w-4" />
                {testMut.isPending ? "Menguji…" : "Test Connection"}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    !form.name ||
                    !form.base_url ||
                    createMut.isPending ||
                    updateMut.isPending
                  }
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {editing ? "Simpan" : "Tambah"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
